import React, { useContext, useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import { ModalTitle, Progress, TradingIsClosed } from '@trader/components';
import { TModalStore, TOrderMetricEntity, useMst } from '@trader/store';
import { IInstrumentDetailsBE, IOrderDetailsBE } from '@trader/api';
import { TradingContext } from '@trader/contexts';
import { useBusinessLogic } from '@trader/hooks';
import { useI18next } from '@trader/services';

import {
  IOrderMetricStore,
  TradingModalHeader,
} from '../components/tradingModalHeader';
import { LimitOrder } from '../createOrder/components/LimitOrder';

import * as Styled from './styled';

export const EditOrder: React.FC = observer(() => {
  const store = useMst();
  const { translate } = useI18next();
  const {
    clearPreviousState,
    handleStartTrading,
    instrument: { general, limit },
    handleChangeStopLimit,
    toggleEditField,
  } = useContext(TradingContext);

  const { applyCondition: applyConditionThird } =
    useBusinessLogic('third_case');

  const modal: TModalStore = store.ui.modal;
  const orderId = modal.options.get('orderId');
  const symbol = modal.options.get('symbol');
  const type = modal.options.get('type');

  const freeMargin = store.user.portfolio.freeMargin;

  const metric = store.entities.ordersMetrics.get<TOrderMetricEntity>(orderId);

  const getInstrumentDetails = async () => {
    if (symbol) {
      const [detailsResult, orderDetailsResult] =
        (await store.pages.trading.getAllInstrumentDetailsAsync.run(
          symbol
        )) as [IInstrumentDetailsBE, IOrderDetailsBE];
      const limitOrderBuyValue = metric?.stopPrice;
      const limitOrderSellValue = metric.limitPrice;

      limitOrderBuyValue && toggleEditField('limitBuy', true);
      limitOrderSellValue && toggleEditField('limitSell', true);

      handleStartTrading(
        {
          ...detailsResult,
          ...orderDetailsResult,
          limitOrderBuyValue,
          limitOrderSellValue,
        },
        type
      );
    }
  };

  const handleEditOrder = () => {
    metric?.editOrderAsync.run({
      orderId,
      body: {
        side: limit.billType,
        quantity: Number(limit.amount),
        symbol: general.symbol,
        type: limit.billType === 'Sell' ? 'EntryStop' : 'EntryLimit',
        price:
          limit.billType === 'Sell'
            ? Number(limit.sellValue)
            : Number(limit.buyValue),
      },
    });
  };

  useEffect(() => {
    getInstrumentDetails();
    return () => {
      clearPreviousState();
    };
  }, [symbol]);

  // const name = metric?.side === 'Buy' ? 'buyValue' : 'sellValue';

  useEffect(() => {
    handleChangeStopLimit(
      metric?.quantity,
      'amount',
      'limitAmountError',
      'plus'
    );
  }, [general.symbol, metric?.quantity]);

  if (!general.symbol) {
    return (
      <Styled.Root>
        <Progress />
      </Styled.Root>
    );
  }

  if (applyConditionThird().status === 'failed') {
    return (
      <Styled.Root>
        <TradingIsClosed
          iconTypeName='warning'
          title={translate('WARNINGS.TRADING_IS_DISABLED')}
          content={translate('WARNINGS.PLEASE_CONTACT_SUPPORT', {
            email: import.meta.env.VITE_SUPPORT_EMAIL,
          })}
          buttonContent={translate('COMMON.LABELS.PROCEED')}
          onClick={applyConditionThird()?.action}
        />
      </Styled.Root>
    );
  }

  return (
    <Styled.Root>
      <ModalTitle title={translate('COMMON.LABELS.EDIT_ORDER')} />
      <TradingModalHeader<IOrderMetricStore> type='order' metric={metric} />
      <LimitOrder
        isEditingOrder
        freeMargin={freeMargin}
        buttonTitle={translate('COMMON.LABELS.EDIT_ORDER')}
        onClick={handleEditOrder}
      />
    </Styled.Root>
  );
});
